import type {
  FolderAnalysisEvidence,
  FolderImportRequest,
  FolderImportStagingResult,
  FolderSelection,
  ProcessResult,
} from '../shared/desktop-api';
import type { EngineCall } from './desktop-commands';
import { fetchEngine } from './engine-call';
import { pickFiles, pickFolder, trackFolder, type PickedFiles } from './folder-picker';
import type { JobChannels } from './job-channels';
import { isJobChannel } from './job-channel';
import type { BrowserCaptures } from './browser-captures';
import {
  folderProgress,
  followRun,
  isTicket,
  progressFor,
  runProcess,
  type RawProgress,
  type RunTicket,
} from './process-runs';
import { clearUploadLine, uploadFiles, type UploadChannel, type UploadReceipt } from './upload';
import { arrivedFileCount, importArrived, takeArrived } from './arrived-setup';
import { rememberRun, rememberedRun } from './run-memory';
import { analyses, evidenceOf, statusOf, type Analysis } from './folder-analyses';
import { dropProposal, keepProposal, restoreProposal } from './setup-evidence';
import { findSetup, recoverIfHeld, resumeSetup } from './setup-resume';

/**
 * Setup from samples, in the browser: what the desktop's main process did around a representative
 * folder or file selection (main.ts `importFolderDocuments`). The picker keeps the chosen files
 * under a token; when the App asks for the analysis the files go up through the job's channel and
 * the engine's `process` runs over them with no document types, which is the request that yields
 * a proposal. It is kept for the page's lifetime as the desktop's sidecar was, so a route change
 * while it runs finds it again and reconciles the proposal (App.tsx `hydrateRepresentativeEvidence`).
 */
export function folderAnalysis(
  channels: JobChannels,
  stageFolder: BrowserCaptures['stageWaves'],
): Record<string, unknown> {
  return {
    chooseRepresentativeFiles: async (): Promise<FolderSelection> => {
      // "Set up from these documents": what a program already sent is the sample, no picker (arrived-setup.ts).
      if (takeArrived()) return keepArrived(arrivedFileCount());
      const files = await pickFiles();
      return keep({ files, folderName: '', skipped: [] });
    },
    chooseDocumentFolder: async (): Promise<FolderSelection> =>
      keep(trackFolder(await pickFolder())),
    importFolderDocuments: (request: FolderImportRequest) => analyze(channels, request),
    // "Resume": the unfinished setup again from the documents the workspace holds (setup-resume.ts).
    resumeFolderAnalysis: (jobId: string) =>
      resumeSetup(callOn(channels, jobId), jobId, settle, noteProgress),
    // A reload finds the run still going on the host (ProcessRuns) and follows it from here.
    getFolderAnalysisStatus: async (jobId: string) => {
      await rejoin(channels, jobId);
      return statusOf(jobId);
    },
    // The App asks for the evidence, not the status, when a job opens on its setup (App.tsx
    // `hydrateRepresentativeEvidence`): a reload that only reattached on the status call found
    // nothing running and put the person on an empty Document types editor mid-setup.
    getFolderAnalysisEvidence: async (jobId: string): Promise<FolderAnalysisEvidence> => {
      await rejoin(channels, jobId);
      return evidenceOf(jobId);
    },
    discardFolderAnalysis: async (jobId: string) => {
      analyses.delete(jobId);
      await dropProposal(callOn(channels, jobId), jobId);
      return statusOf(jobId);
    },
    ...setupEvidence(channels),
    ...setupOperation(channels),
    stageFolderWaves: (request: { jobId: string; token: string }) =>
      stageWaves(stageFolder, request),
  };
}

/**
 * A folder for a job that already has its setup: CapturePoint's waves (folder-waves.ts), staged at
 * once and uploaded one after another (wave-uploads.ts). It was one capture of the whole folder, so
 * nothing reached review until every file had been read, and the screen counted no waves.
 */
async function stageWaves(
  stage: BrowserCaptures['stageWaves'],
  request: { jobId: string; token: string },
): Promise<FolderImportStagingResult> {
  const picked = selections.get(request.token);
  selections.delete(request.token);
  if (!picked) throw new Error('The selected folder session expired. Choose the folder again.');
  return stage(request.jobId, picked);
}

/** The reviewer's own amendments to a proposal, kept where the proposal is. */
function setupEvidence(channels: JobChannels): Record<string, unknown> {
  return {
    // The reviewer reassigning a sample's kind on the proposal screen. CapturePoint keeps the
    // amended proposal in its sidecar so a reload finds the reviewer's work rather than the
    // reader's first guess; Paige's sidecar is this map for the page and the job's engine host
    // for a reload (setup-evidence.ts, parity S-13). A host that refuses is said on screen
    // ("The sample assignment was not saved"): the page still shows the change, a reload would not.
    saveFolderAnalysisEvidence: async (request: { jobId: string; result: ProcessResult }) => {
      const analysis = analyses.get(request.jobId);
      if (!analysis)
        throw new Error(
          'That sample is no longer open. Choose the documents again to set up this job.',
        );
      analysis.result = request.result;
      analysis.evidenceRevision += 1;
      await keepProposal(callOn(channels, request.jobId), request.jobId, request.result);
    },
  };
}

/**
 * What the desktop's durable setup operation offered and Paige's page-lifetime one still has to
 * answer for. Split out because the two are the same control on screen and a different thing
 * underneath: CapturePoint's setup is a record on disk that can be stopped, resumed or abandoned;
 * Paige's is the promise above. Every one of these resolved `undefined` before — and a failed
 * sample is exactly when the screen offers them, because a failure is the only state that puts
 * the decision block up with the analysis still named.
 */
function setupOperation(channels: JobChannels): Record<string, unknown> {
  return {
    // "Choose documents again" and "Stop setup", the two buttons the decision block puts up.
    abandonFolderAnalysisOperation: async (request: { jobId: string }) => {
      analyses.delete(request.jobId);
      return statusOf(request.jobId);
    },
    stopFolderAnalysisOperation: async (request: { jobId: string }) => {
      const analysis = analyses.get(request.jobId);
      // The host stops the reading (cancel-process): nothing more is read or saved, and no pages
      // are charged for what was not read. The screen's wait ends at once as a stop, and the run's
      // own answer, when it comes, cannot turn the stopped setup back into a proposal.
      // Only the run this tab started (its remembered id); with none yet, the files are still going
      // up and the flag below keeps the reading from being asked for at all.
      const runId = rememberedRun(request.jobId);
      if (runId)
        await callOn(channels, request.jobId)('cancel-process', {
          jobId: request.jobId,
          runId,
        }).catch(() => undefined);
      rememberRun(request.jobId, null);
      if (analysis) {
        analysis.state = 'failed';
        analysis.failure = 'Setup was stopped. Choose the documents again when you are ready.';
        analysis.stopped = true;
        analysis.stop?.();
      }

      return statusOf(request.jobId);
    },
    // Accepting a proposal. On the desktop the sample is a sample and the rest of the folder is
    // still waiting, so this is where the remainder is queued. Paige sets a job up from every
    // document it was given (Dustin, 2026-10-03: no "Quick setup" of the first 100), so nothing is
    // left to queue and the count the toast prints is what is already in.
    stageFolderImport: async (jobId: string): Promise<FolderImportStagingResult> => ({
      captures: [],
      totalFileCount: analyses.get(jobId)?.totalFileCount ?? 0,
    }),
  };
}

/** How a stopped setup comes back to the screen: the words its `canceledByStop` knows as a stop. */
const stoppedByPerson = () => new Error('A task was canceled.');

/** Whether this tab is reading the job's setup sample, or still holds how that reading ended. */
export const setupInHand = (jobId: string): boolean =>
  analyses.has(jobId) || Boolean(rememberedRun(jobId));

/** Leaving a job forgets a setup that ended without a proposal: it starts over (unfinished-setup.ts). */
export function forgetEndedSetup(jobId: string): void {
  const state = analyses.get(jobId)?.state;
  if (state !== 'running' && state !== 'ready') analyses.delete(jobId);
}

/** The picked selections, by token, until the App asks for them. */
const selections = new Map<string, PickedFiles>();

function keep(picked: PickedFiles): FolderSelection {
  if (picked.files.length === 0 && picked.skipped.length === 0) return { canceled: true };
  if (picked.files.length === 0)
    throw new Error(
      `None of the ${picked.skipped.length.toLocaleString()} files in ${picked.folderName || 'the selection'} are PDFs or images`,
    );
  const token = crypto.randomUUID();
  selections.set(token, picked);
  return {
    canceled: false,
    token,
    folderName: picked.folderName,
    supportedFileCount: picked.files.length,
    analysisFileCount: picked.files.length,
  };
}

/** A selection that stands for the documents already in Paige: nothing to upload, the API moves them. */
function keepArrived(count: number): FolderSelection {
  const token = crypto.randomUUID();
  selections.set(token, { files: [], folderName: '', skipped: [] });
  arrivedSelections.set(token, count);
  return {
    canceled: false,
    token,
    folderName: '',
    supportedFileCount: count,
    analysisFileCount: count,
  };
}
const arrivedSelections = new Map<string, number>();

async function analyze(
  channels: JobChannels,
  request: FolderImportRequest,
): Promise<ProcessResult> {
  const picked = selections.get(request.token);
  if (!picked) throw new Error('The selected files are no longer waiting. Choose them again.');
  selections.delete(request.token);
  const arrived = arrivedSelections.get(request.token);
  arrivedSelections.delete(request.token);
  const analysis: Analysis = {
    state: 'running',
    startedAt: new Date().toISOString(),
    totalFileCount: arrived ?? picked.files.length,
    analyzedFileCount: 0,
    result: null,
    failure: null,
    evidenceRevision: 0,
    progress: null,
  };
  analyses.set(request.jobId, analysis);
  const stopped = new Promise<never>((_, reject) => {
    analysis.stop = () => reject(stoppedByPerson());
  });
  try {
    return await settle(
      request.jobId,
      analysis,
      Promise.race([readSample(channels, request, arrived === undefined ? picked : null), stopped]),
      callOn(channels, request.jobId),
    );
  } finally {
    window.paigeWorkbench?.upload?.(null);
  }
}

/**
 * The reading's end into the analysis: the proposal, or the failure in the engine's words. A failure
 * that was not the person's stop is offered as Resume when the host can run the setup again (`call`).
 */
async function settle(
  jobId: string,
  analysis: Analysis,
  reading: Promise<ProcessResult>,
  call?: EngineCall,
): Promise<ProcessResult> {
  try {
    const result = await reading;
    if (analysis.stopped) throw stoppedByPerson();
    analysis.analyzedFileCount = analysis.totalFileCount;
    analysis.result = result;
    analysis.state = result.bootstrapProposal?.documentTypes.length ? 'ready' : 'no-proposal';
    return result;
  } catch (reason) {
    // A stopped setup keeps its own words; the reading's failure (the host's "stopped") says nothing new.
    if (analysis.state !== 'failed' || !analysis.failure) {
      analysis.state = 'failed';
      analysis.failure = reason instanceof Error ? reason.message : 'The sample could not be read';
      if (call && !analysis.stopped) await recoverIfHeld(call, jobId);
    }
    throw reason;
  } finally {
    rememberRun(jobId, null);
  }
}

/** The host's latest word on the reading, onto the analysis and out to the screen's subscription. */
function noteProgress(jobId: string, raw: RawProgress, ticket: RunTicket): void {
  const analysis = analyses.get(jobId);
  const progress = progressFor(raw, jobId);
  if (analysis) {
    analysis.progress = progress;
    if (progress.total > analysis.totalFileCount) analysis.totalFileCount = progress.total;
    if (ticket.startedAt) analysis.startedAt = ticket.startedAt;
  }
  folderProgress.publish(progress);
}

/**
 * The run this tab started before a reload, if the host still has it: put back as running and
 * followed to its end, so setup shows the progress it would have shown. A run the host no longer
 * knows is forgotten and the folder offered again; the tenant's channel holds none (403 there).
 */
function rejoin(channels: JobChannels, jobId: string): Promise<void> {
  if (analyses.has(jobId) || !isJobChannel(channels.channelFor(jobId).engineOrigin))
    return Promise.resolve();
  let joining = rejoining.get(jobId);
  if (!joining) {
    joining = reattach(channels, jobId).finally(() => rejoining.delete(jobId));
    rejoining.set(jobId, joining);
  }
  return joining;
}
const rejoining = new Map<string, Promise<void>>();

async function reattach(channels: JobChannels, jobId: string): Promise<void> {
  const call = callOn(channels, jobId);
  // This tab's own run, or the setup the host has under way for the job (setup-resume.ts).
  const runId = rememberedRun(jobId) ?? (await findSetup(call, jobId));
  if (!runId) {
    // No run to follow: the proposal the reviewer amended before the reload, if the host still
    // keeps it (setup-evidence.ts), unless a setup waits to be resumed.
    if (!analyses.has(jobId)) await restoreProposal(call, jobId);
    return;
  }
  let answer: unknown;
  try {
    answer = await call('process-status', { jobId, runId });
  } catch {
    rememberRun(jobId, null);
    await recoverIfHeld(call, jobId);
    return;
  }
  const analysis: Analysis = {
    state: 'running',
    startedAt: (isTicket(answer) && answer.startedAt) || new Date().toISOString(),
    totalFileCount: 0,
    analyzedFileCount: 0,
    result: null,
    failure: null,
    evidenceRevision: 0,
    progress: null,
  };
  analyses.set(jobId, analysis);
  const reading = isTicket(answer)
    ? followRun<ProcessResult>(call, answer, (raw, ticket) => noteProgress(jobId, raw, ticket))
    : Promise.resolve(answer as ProcessResult);
  // Followed in the background: the status call answers at once with the run as running, instead
  // of waiting out the whole reading (a failure message waited minutes on it).
  void settle(jobId, analysis, reading, call).catch(() => undefined);
}

/** The engine over the job's channel, as the adapter's `call` is. */
function callOn(channels: JobChannels, jobId: string): EngineCall {
  const channel = channels.channelFor(jobId);
  return <T>(command: string, args: Record<string, unknown> = {}) =>
    fetchEngine<T>(channel, command, args);
}

/** The chosen files up through the job's channel; the workspace's receipt for them. */
async function sendUp(channel: UploadChannel, picked: PickedFiles): Promise<UploadReceipt> {
  const receipt = await uploadFiles(
    channel,
    picked.files,
    (progress) => window.paigeWorkbench?.upload?.({ ...progress, skipped: picked.skipped }),
    'folder',
  );
  // What the folder held that Paige does not read, and what it took in and could not: both stay on
  // the line for a moment, because the rest of the folder went in without them (ADR-0012).
  clearUploadLine({
    sent: picked.files.length,
    total: picked.files.length,
    skipped: picked.skipped,
    unreadable: receipt.skipped,
  });
  if (receipt.files.length === 0) throw new Error('None of the chosen files are PDFs or images');
  return receipt;
}

/**
 * The files into the job's workspace (uploaded, or, with no selection, moved there by the API from what
 * arrived), then the engine's `process` with no types: the proposal.
 */
async function readSample(
  channels: JobChannels,
  request: FolderImportRequest,
  picked: PickedFiles | null,
): Promise<ProcessResult> {
  const channel = channels.channelFor(request.jobId);
  const receipt = picked ? await sendUp(channel, picked) : await importArrived(channel);
  if (receipt.files.length === 0)
    throw new Error('Nothing is waiting for this job any more. Choose documents to set it up.');
  // Stopped while the files were going up: the reading is never asked for.
  if (analyses.get(request.jobId)?.stopped) throw stoppedByPerson();
  const args = {
    jobId: request.jobId,
    files: receipt.files,
    documentTypes: [],
    acquisitionId: receipt.token,
  };
  // The run is remembered by the request's id, which is the run's (ProcessRuns), before it starts.
  const id = crypto.randomUUID();
  rememberRun(request.jobId, id);
  return runProcess<ProcessResult>(
    callOn(channels, request.jobId),
    { ...args, id },
    (raw, ticket) => noteProgress(request.jobId, raw, ticket),
  );
}
